

export interface AdCopy {
  platform: string;
  headline: string;
  body: string;
}

export interface InvestorPitch {
  companyPurpose: string;
  problem: string;
  solution: string;
  whyNow: string;
  marketPotential: string;
  competition: string;
  productOverview: string;
  businessModel: string;
  team: string;
  financials: string;
  vision: string;
  callToAction: string;
}

export interface GrowthStrategy {
  acquisition: string;
  activation: string;
  retention: string;
  referral: string;
  revenue: string;
}

export interface KeyMoments {
  setupMoment: string;
  ahaMoment: string;
  habitMoment: string;
}

export interface IdeaDetails {
  // Core Product
  productForm: string;
  problemToSolve: string;
  potentialSolution: string;
  mvpValidation: string;
  howItWorks: string;
  userExperience: string;
  
  // Service Design
  serviceDesign: string;

  // Market
  marketEstimate: string;
  totalMarketSizeMM?: number; // Numeric Market Size in Millions
  competitors: string[];
  marketSizeScore: number;
  timeToMarketMonths: number;

  // Business Model Canvas Blocks
  keyPartners: string[];
  keyActivities: string[];
  keyResources: string[];
  uniqueValueProposition: string;
  customerRelationships: string;
  channels: string[];
  targetCustomers: string;
  costStructure: string;
  detailedCostAnalysis: string;
  revenueStreams: string;

  // Marketing & Growth
  goToMarketStrategy: string;
  marketingUserJourney: string[];
  landingPageCopy: string;
  adCopies: AdCopy[];
  
  // AARRR & Mechanics
  growthStrategy?: GrowthStrategy;
  keyMoments?: KeyMoments;

  // Pitch
  investorPitch?: InvestorPitch;

  // Legacy mappings
  businessModel?: string; 
}

export interface MetricEntry {
  id: string;
  date: number;
  mrr?: number;
  websiteTraffic?: number;
  conversionRate?: number;
  custom?: Record<string, number>;
}

export interface Idea {
  id: string;
  userId?: string;
  title: string;
  description: string;
  createdAt: number;
  updatedAt: number;
  isArchived: boolean;
  isPublic: boolean; 
  systemPromptVersion?: number; // Track which version of AI generated this
  icon?: string;
  logo?: string;
  landingPageDesign?: string;
  landingPageDesigns?: string[];
  productConcepts?: string[];
  details?: IdeaDetails;
  metrics?: MetricEntry[]; // Traction history
  status: 'draft' | 'researched' | 'launched';
}

export interface IdeaVersion {
  id: string;
  ideaId: string;
  savedAt: number;
  data: Idea;
}

export type ViewMode = 'shelf' | 'editor' | 'compare' | 'explore';

export interface SortOption {
  field: keyof Idea | 'marketSizeScore' | 'timeToMarketMonths';
  direction: 'asc' | 'desc';
}

// --- Freemium & Plans ---

export type PlanTier = 'free' | 'starter' | 'pro' | 'executive';

export interface MonthlyAllowance {
  limit: number;
  used: number;
  plan: PlanTier;
  resetAt?: number;
}

export interface UserProfile {
  uid: string;
  email: string;
  plan: PlanTier;
  ideasCreated: number; 
  tokensUsed: number; // Lifetime usage
  subscriptionStatus: 'active' | 'canceled' | 'expired';
  periodEnd: number; 
  customMetricDefinitions?: string[]; 
  
  // Onboarding Data
  onboardingCompleted?: boolean;
  role?: string;
  company?: string;
}

export const CURRENT_SYSTEM_PROMPT_VERSION = 3;

export interface PlanLimits {
    credits: number; // Weighted Credits (1 Flash = 1 Credit, 1 Pro = 10 Credits)
    ideas: number; // Max active ideas
    label: string;
    price: number;
    interval: 'mo' | 'yr' | '';
    privateIdeas: boolean;
    imageGeneration: boolean;
    prioritySupport: boolean;
    models: ('flash' | 'pro')[];
}

export const PLAN_LIMITS: Record<PlanTier, PlanLimits> = {
  free: { 
    credits: 10000,
    ideas: 3,
    label: 'Free',
    price: 0,
    interval: '',
    privateIdeas: false,
    imageGeneration: false,
    prioritySupport: false,
    models: ['flash']
  },
  starter: { 
    credits: 500000,
    ideas: 100,
    label: 'Starter', 
    price: 12,
    interval: 'mo',
    privateIdeas: true,
    imageGeneration: false,
    prioritySupport: false,
    models: ['flash']
  },
  pro: { 
    credits: 2500000, 
    ideas: Number.MAX_SAFE_INTEGER,
    label: 'Pro', 
    price: 29,
    interval: 'mo',
    privateIdeas: true,
    imageGeneration: true,
    prioritySupport: true,
    models: ['flash', 'pro']
  },
  executive: { 
    credits: 6000000, 
    ideas: Number.MAX_SAFE_INTEGER,
    label: 'Executive', 
    price: 49,
    interval: 'mo',
    privateIdeas: true,
    imageGeneration: true,
    prioritySupport: true,
    models: ['flash', 'pro']
  }
};

export const TOKEN_COSTS = {
  RESEARCH_FULL: 3000,
  IMAGE_GEN: 1000,
  TEXT_REGEN: 200,
  PITCH_DECK: 500
};